import { Size, TAG_CATEGORY_ICONS, TAG_CATEGORY_ICON_MEANINGS, TAG_COLOURS, isTagCategoryIcon, t, tagColourOrDefault, tagLook, tagTooltip, type DrawnTag, type StringKey, type TagCategoryIcon, type TagColour } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import styles from './TagSwatch.module.css';

/**
 * The tag chip and the picker that colours it (piece 13; product owner,
 * 2026-09-14: *"Can't we set tag colour?"* and *"Grey blends."*).
 *
 * 🔨 A swatch is a token NAME, never a hex. The name resolves to the pair
 * `var(--<name>-text)` and `var(--<name>-bg)` at draw time, so re-tuning a
 * swatch for contrast is a token edit and never a data migration.
 *
 * 💰 The chip is outlined, and that is the fix for "grey blends" rather than a
 * darker grey. Every tint in the palette reads between 1.1:1 and 1.3:1 against
 * the surfaces a chip sits on, so no fill of any hue can say where the chip
 * ends. The edge, drawn in the swatch's own text hex at 4.8:1 or better, can.
 */
export const TAG_COLOUR_LABEL_KEYS: Record<TagColour, StringKey> = {
  'tag-slate': 'tag.colour.slate',
  'tag-blue': 'tag.colour.blue',
  'tag-teal': 'tag.colour.teal',
  'tag-green': 'tag.colour.green',
  'tag-amber': 'tag.colour.amber',
  'tag-orange': 'tag.colour.orange',
  'tag-red': 'tag.colour.red',
  'tag-purple': 'tag.colour.purple',
};

/** The token pair a stored colour draws in; an absent or unknown name falls back to the default. */
export function tagPair(colour: string | null | undefined): { foreground: string; background: string; swatch: TagColour } {
  const swatch = tagColourOrDefault(colour);
  return { foreground: `var(--${swatch}-text)`, background: `var(--${swatch}-bg)`, swatch };
}

export interface TagChipProps {
  label: string;
  /** A stored swatch name, or null/undefined where nobody has picked one. */
  colour?: string | null | undefined;
  size?: Size;
  /** 🔒 The glyph before the label: the category's, or the plain tag for one with none (decision 2260). */
  icon?: string | null | undefined;
  /** The hover text, *Category: Tag* and the record's note beneath (decisions 2260, 2261). */
  title?: string | null | undefined;
}

/**
 * One tag drawn as it appears on a record banner and in the Settings list: the
 * stadium of the Pill, outlined in its own foreground, with the icon before the
 * label (decision 2260). 🔨 The label is drawn as typed, never Title Cased,
 * because a person coined it (decision 1110).
 */
export function TagChip({ label, colour, size = Size.Medium, icon, title }: TagChipProps) {
  const { foreground, background, swatch } = tagPair(colour);
  const glyph: TagCategoryIcon = isTagCategoryIcon(icon) ? icon : 'tag';
  return (
    <span className={styles.chip} data-tag-chip={swatch} data-tag-icon={glyph} {...(title ? { title } : {})}>
      <span className={`${styles.tagPill} ${size === Size.Small ? styles.tagPillSmall : styles.tagPillMedium}`} style={{ color: foreground, background }} data-size={size}>
        <Icon meaning={TAG_CATEGORY_ICON_MEANINGS[glyph]} size="inline" />
        <span>{label}</span>
      </span>
    </span>
  );
}

/**
 * 🔒 A tag as its record draws it (decisions 2260, 2261): the category's color
 * and icon while the category is in use, its own color and the plain tag icon
 * otherwise, and the tooltip *Category: Tag* with the note beneath.
 */
export function TagPill({ tag, size = Size.Medium }: { tag: DrawnTag; size?: Size }) {
  const look = tagLook(tag);
  return <TagChip label={tag.label} colour={look.colour} icon={look.icon} size={size} title={tagTooltip(tag, t('tag.noCategory'))} />;
}

export interface TagIconPickerProps {
  value: string | null;
  onChange: (next: TagCategoryIcon) => void;
  name: string;
  legend?: string;
}

/**
 * 🔒 The sixteen category icons as native radios (decision 2260), drawn as the
 * editor's eight-across grid; the same keyboard and screen-reader behavior the
 * swatch picker has, with each glyph's name read out.
 */
export function TagIconPicker({ value, onChange, name, legend }: TagIconPickerProps) {
  return (
    <fieldset className={styles.picker} data-tag-icon-picker={value ?? ''}>
      <legend className={styles.legend}>{legend ?? t('tag.icon.legend')}</legend>
      <div className={styles.icons}>
        {TAG_CATEGORY_ICONS.map((icon) => {
          const id = `${name}-${icon}`;
          return (
            <span key={icon} className={styles.option}>
              <input className={styles.input} type="radio" id={id} name={name} value={icon} checked={value === icon} onChange={() => onChange(icon)} />
              <label className={styles.iconChoice} htmlFor={id} data-tag-icon-choice={icon}>
                <Icon meaning={TAG_CATEGORY_ICON_MEANINGS[icon]} size="header" />
                <span className={styles.swatchName}>{icon}</span>
              </label>
            </span>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface TagSwatchPickerProps {
  /** The chosen swatch; null means nobody has picked one and the default draws. */
  value: string | null;
  onChange: (next: TagColour) => void;
  /** The radio group's name, so two pickers on one screen do not share a selection. */
  name: string;
  legend?: string;
}

/**
 * The eight swatches as native radios, so arrow keys, the tab order and a
 * screen reader all work without the component re-implementing any of them. The
 * input is hidden visually and stays focusable; the label is the swatch.
 */
export function TagSwatchPicker({ value, onChange, name, legend }: TagSwatchPickerProps) {
  const chosen = tagColourOrDefault(value);
  return (
    <fieldset className={styles.picker} data-tag-swatch-picker={chosen}>
      <legend className={styles.legend}>{legend ?? t('tag.colour.legend')}</legend>
      <div className={styles.swatches}>
        {TAG_COLOURS.map((swatch) => {
          const id = `${name}-${swatch}`;
          return (
            <span key={swatch} className={styles.option}>
              <input
                className={styles.input}
                type="radio"
                id={id}
                name={name}
                value={swatch}
                checked={chosen === swatch}
                onChange={() => onChange(swatch)}
              />
              <label className={styles.swatch} htmlFor={id} data-tag-swatch={swatch} style={{ '--swatch-text': `var(--${swatch}-text)`, '--swatch-bg': `var(--${swatch}-bg)` } as React.CSSProperties}>
                <span className={styles.swatchName}>{t(TAG_COLOUR_LABEL_KEYS[swatch])}</span>
              </label>
            </span>
          );
        })}
      </div>
    </fieldset>
  );
}
